
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Toaster } from "@/components/ui/toaster";
import { ShoppingCart, Heart, Shield, Leaf, Award, Menu, X } from "lucide-react";

function App() {
  const { toast } = useToast();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleAddToCart = () => {
    toast({
      title: "Added to cart!",
      description: "Gutvia has been added to your shopping cart.",
    });
  };

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const menuItems = [
    { name: "Home", href: "#" },
    { name: "Products", href: "#products" },
    { name: "About", href: "#about" },
    { name: "Testimonials", href: "#testimonials" },
    { name: "Contact", href: "#contact" }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed w-full bg-white/90 backdrop-blur-sm z-50 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <a href="#" className="text-2xl font-bold text-primary">Gutvia</a>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              {menuItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-gray-600 hover:text-primary transition-colors"
                >
                  {item.name}
                </a>
              ))}
              <Button onClick={handleAddToCart} size="sm">
                <ShoppingCart className="w-4 h-4 mr-2" />
                Cart
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-600 hover:text-primary"
              >
                {isMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="md:hidden py-4"
            >
              <div className="flex flex-col space-y-4">
                {menuItems.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="text-gray-600 hover:text-primary transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </a>
                ))}
                <Button onClick={handleAddToCart} className="w-full">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Cart
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section relative min-h-screen flex items-center pt-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeIn}
            className="max-w-2xl"
          >
            <h1 className="text-5xl font-bold text-primary mb-6">
              Support Your Pet's Gut Health Naturally
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Premium gut health supplements made from purified vermicast, 
              scientifically formulated for dogs, cats, and horses.
            </p>
            <Button
              size="lg"
              onClick={handleAddToCart}
              className="text-lg"
            >
              Try Gutvia Today
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="about" className="py-20 bg-secondary/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">
            Why Choose Gutvia?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              className="benefit-card"
              whileHover={{ scale: 1.05 }}
            >
              <Shield className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Vet Approved</h3>
              <p className="text-gray-600">
                Formulated and tested by veterinary professionals
              </p>
            </motion.div>

            <motion.div
              className="benefit-card"
              whileHover={{ scale: 1.05 }}
            >
              <Leaf className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">100% Natural</h3>
              <p className="text-gray-600">
                Pure vermicast extract with no artificial additives
              </p>
            </motion.div>

            <motion.div
              className="benefit-card"
              whileHover={{ scale: 1.05 }}
            >
              <Award className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Premium Quality</h3>
              <p className="text-gray-600">
                Made in USA under strict quality control
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section id="products" className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">
            Our Products
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              className="product-card"
              whileHover={{ scale: 1.02 }}
            >
              <div className="aspect-square relative">
                <img alt="Dog supplement bottle" className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1698077153827-3270e409e524" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Gutvia for Dogs</h3>
                <p className="text-gray-600 mb-4">
                  Specially formulated for canine digestive health
                </p>
                <Button onClick={handleAddToCart} className="w-full">
                  Add to Cart
                </Button>
              </div>
            </motion.div>

            <motion.div
              className="product-card"
              whileHover={{ scale: 1.02 }}
            >
              <div className="aspect-square relative">
                <img alt="Cat supplement bottle" className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1682421213202-64fd69e9e344" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Gutvia for Cats</h3>
                <p className="text-gray-600 mb-4">
                  Perfect formula for feline gut health
                </p>
                <Button onClick={handleAddToCart} className="w-full">
                  Add to Cart
                </Button>
              </div>
            </motion.div>

            <motion.div
              className="product-card"
              whileHover={{ scale: 1.02 }}
            >
              <div className="aspect-square relative">
                <img alt="Horse supplement bottle" className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1599053581540-248ea75b59cb" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Gutvia for Horses</h3>
                <p className="text-gray-600 mb-4">
                  Advanced formula for equine digestive support
                </p>
                <Button onClick={handleAddToCart} className="w-full">
                  Add to Cart
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 bg-secondary/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">
            What Pet Parents Say
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              className="testimonial-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center mb-4">
                <img alt="Customer avatar" className="w-12 h-12 rounded-full mr-4" src="https://images.unsplash.com/photo-1698289388720-fafd0f2dc728" />
                <div>
                  <h4 className="font-semibold">Sarah M.</h4>
                  <p className="text-gray-600">Dog Owner</p>
                </div>
              </div>
              <p className="text-gray-600">
                "My dog's digestive issues improved significantly after using Gutvia. 
                I love that it's all-natural and vet-approved!"
              </p>
            </motion.div>

            <motion.div
              className="testimonial-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center mb-4">
                <img alt="Customer avatar" className="w-12 h-12 rounded-full mr-4" src="https://images.unsplash.com/photo-1554687364-37d2fdd2fb4f" />
                <div>
                  <h4 className="font-semibold">Michael R.</h4>
                  <p className="text-gray-600">Cat Owner</p>
                </div>
              </div>
              <p className="text-gray-600">
                "Finally found a natural supplement that my cat actually accepts! 
                Her coat looks better and she's more energetic."
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">Get In Touch</h2>
          <div className="max-w-2xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-4">Contact Information</h3>
                <p className="text-gray-600 mb-4">
                  Have questions about Gutvia? Our team is here to help!
                </p>
                <div className="space-y-2">
                  <p className="text-gray-600">Email: support@gutvia.com</p>
                  <p className="text-gray-600">Phone: (555) 123-4567</p>
                  <p className="text-gray-600">Hours: Mon-Fri, 9am-5pm EST</p>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4">Follow Us</h3>
                <p className="text-gray-600 mb-4">
                  Stay updated with our latest news and promotions.
                </p>
                <div className="flex space-x-4">
                  <a href="#" className="text-gray-600 hover:text-primary">Facebook</a>
                  <a href="#" className="text-gray-600 hover:text-primary">Instagram</a>
                  <a href="#" className="text-gray-600 hover:text-primary">Twitter</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div>
              <h3 className="text-xl font-semibold mb-4">About Gutvia</h3>
              <p className="text-white/80">
                {/* Editable company description */}
                Premium pet gut health supplements made with love and science.
                Committed to improving pet wellness naturally.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-xl font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {/* Editable menu items */}
                <li><a href="#products" className="hover:text-white/80">Shop</a></li>
                <li><a href="#about" className="hover:text-white/80">About Us</a></li>
                <li><a href="#testimonials" className="hover:text-white/80">Reviews</a></li>
                <li><a href="#contact" className="hover:text-white/80">Contact</a></li>
              </ul>
            </div>

            {/* Customer Service */}
            <div>
              <h3 className="text-xl font-semibold mb-4">Customer Service</h3>
              <ul className="space-y-2">
                {/* Editable service links */}
                <li><a href="#" className="hover:text-white/80">Shipping Info</a></li>
                <li><a href="#" className="hover:text-white/80">Returns Policy</a></li>
                <li><a href="#" className="hover:text-white/80">FAQ</a></li>
                <li><a href="#" className="hover:text-white/80">Privacy Policy</a></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-xl font-semibold mb-4">Contact Us</h3>
              <div className="space-y-2 text-white/80">
                {/* Editable contact information */}
                <p>Email: support@gutvia.com</p>
                <p>Phone: (555) 123-4567</p>
                <p>Address: 123 Health Street,<br />Wellness City, WC 12345</p>
              </div>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="border-t border-white/20 mt-8 pt-8 text-center">
            <p className="text-white/60">
              {/* Editable copyright text */}
              © {new Date().getFullYear()} Gutvia. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <Toaster />
    </div>
  );
}

export default App;
